import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, forkJoin, map } from 'rxjs';
import { API_BASE_URL } from '../../../common/tokens/api-base-url.token';
import { AnalysisDetail } from '../../application/entities/analysis-detail.entity';
import { AnalysisHistoryFilters, AnalysisHistoryResult } from '../../application/entities/analysis-history.entity';
import { AnalysisListFilters } from '../../application/entities/analysis-list-filters.entity';
import { AnalysisListResult } from '../../application/entities/analysis-list-result.entity';
import { LocationLevel } from '../../application/entities/location-level.entity';
import { AnalysisProviderPort } from '../../application/ports/analysis-provider.port';
import { SecurityFunction } from '../../application/entities/analysis-task.entity';
import { AccessPoint } from '../../application/entities/analysis-task.entity';
import { ClientOrganization } from '../../application/entities/client-organization.entity';
import { ConsultantUser } from '../../application/entities/consultant-user.entity';
import { AnalysisListResponseDto } from '../dto/analysis-list-response.dto';
import { AnalysisStatsResponseDto } from '../dto/analysis-stats-response.dto';
import { AnalysisHistoryResponseDto } from '../dto/analysis-history-response.dto';
import { AnalysisDetailResponseDto } from '../dto/analysis-detail-response.dto';
import { LocationLevelsResponseDto } from '../dto/location-levels-response.dto';
import {
  PatchAnalysisRequestDto,
  CreateRiskRequestDto,
  UpdateRiskRequestDto,
  CreateRequirementRequestDto,
  UpdateRequirementRequestDto,
  CreateAccessPointRequestDto,
  UpdateAccessPointRequestDto,
  CreateSecurityFunctionRequestDto,
  UpdateSecurityFunctionRequestDto,
  CreateFindingDataDto,
  UpdateFindingDataDto,
} from '../dto/analysis-mutation-request.dto';
import { buildFilterBody, hasActiveFilters, toAnalysisListResult } from '../mappers/analysis-list.mapper';
import { toAnalysisDetail } from '../mappers/analysis.mapper';
import { toAnalysisHistoryResult } from '../mappers/analysis-history.mapper';
import { toLocationLevels } from '../mappers/location-levels.mapper';

@Injectable({ providedIn: 'root' })
export class AnalysisProviderService implements AnalysisProviderPort {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  // ── Queries ──────────────────────────────────────────────

  getById(id: string): Observable<AnalysisDetail> {
    return this.http
      .get<AnalysisDetailResponseDto>(`${this.baseUrl}analyses/${id}`)
      .pipe(map(toAnalysisDetail));
  }

  list(filters?: AnalysisListFilters): Observable<AnalysisListResult> {
    const limit = filters?.limit ?? 10;
    const page = filters?.page ?? 1;
    const init = (page - 1) * limit;
    const params = { init, limit, total: true, orderBy: '-updatedAt' };

    const list$: Observable<AnalysisListResponseDto> = hasActiveFilters(filters)
      ? this.http.post<AnalysisListResponseDto>(
          `${this.baseUrl}analyses/get`,
          buildFilterBody(filters!),
          { params },
        )
      : this.http.get<AnalysisListResponseDto>(`${this.baseUrl}analyses`, { params });

    // Stats SIEMPRE hace POST con los mismos filtros que list (para coherencia)
    const stats$ = this.http.post<AnalysisStatsResponseDto>(
      `${this.baseUrl}analyses/stats`,
      buildFilterBody(filters || {}),
    );

    return forkJoin({ list: list$, stats: stats$ }).pipe(
      map(({ list, stats }) => toAnalysisListResult(list, stats, filters)),
    );
  }

  getHistory(
    analysisId: string,
    filters: AnalysisHistoryFilters,
    init: number,
    limit: number,
  ): Observable<AnalysisHistoryResult> {
    // Solo se envían los filtros con valor (el backend ignora los ausentes).
    const params: Record<string, string | number | boolean> = {
      init,
      limit,
      total: true,
      orderBy: '-occurredAt',
    };
    (['entityType', 'action', 'actor', 'from', 'to'] as const).forEach((key) => {
      const value = filters[key]?.trim();
      if (value) params[key] = value;
    });

    return this.http
      .get<AnalysisHistoryResponseDto>(`${this.baseUrl}analyses/${analysisId}/history`, { params })
      .pipe(map((dto) => toAnalysisHistoryResult(dto, init, limit)));
  }

  exportExcel(filters?: AnalysisListFilters): Observable<Blob> {
    return this.http.post(
      `${this.baseUrl}analyses/export`,
      buildFilterBody(filters || {}),
      { responseType: 'blob' },
    );
  }

  getLocationLevels(): Observable<LocationLevel[]> {
    return this.http
      .get<LocationLevelsResponseDto>(`${this.baseUrl}locations`)
      .pipe(map(toLocationLevels));
  }

  getLocationLevelsByOrg(organizationUuid: string): Observable<LocationLevel[]> {
    return this.http
      .get<LocationLevelsResponseDto>(`${this.baseUrl}locations/${organizationUuid}`)
      .pipe(map(toLocationLevels));
  }

  getClientOrganizations(): Observable<ClientOrganization[]> {
    return this.http
      .get<{ data: { organizations: { uuid: string; name: string; listingNumber: string | null }[] } }>(`${this.baseUrl}organizations/clients`)
      .pipe(map(r => (r.data?.organizations ?? []).map(o => ({ uuid: o.uuid, name: o.name, listingNumber: o.listingNumber ?? null }))));
  }

  getConsultants(): Observable<ConsultantUser[]> {
    return this.http
      .get<{ data: { consultants: { uuid: string; firstName: string; lastName: string }[] } }>(`${this.baseUrl}users/consultants`)
      .pipe(map(r => (r.data?.consultants ?? []).map(u => ({
        uuid: u.uuid,
        fullName: `${u.firstName} ${u.lastName}`.trim(),
      }))));
  }

  getOperators(): Observable<ConsultantUser[]> {
    return this.http
      .get<{ data: { operators: { uuid: string; firstName: string; lastName: string }[] } }>(`${this.baseUrl}users/operators`)
      .pipe(map(r => (r.data?.operators ?? []).map(u => ({
        uuid: u.uuid,
        fullName: `${u.firstName} ${u.lastName}`.trim(),
      }))));
  }

  getSecurityFunctions(analysisId: string): Observable<SecurityFunction[]> {
    return this.http
      .get<{ data: { securityFunctions: SecurityFunction[] } }>(
        `${this.baseUrl}analyses/${analysisId}/security-functions`,
      )
      .pipe(map(r => (r.data?.securityFunctions ?? []).map(sf => ({ ...sf, id: (sf as any).uuid }))));
  }

  getAccessPoints(analysisId: string): Observable<AccessPoint[]> {
    return this.http
      .get<{ data: { accessPoints: AccessPoint[] } }>(
        `${this.baseUrl}analyses/${analysisId}/access-points`,
      )
      .pipe(map(r => (r.data?.accessPoints ?? []).map(ap => ({ ...ap, id: (ap as any).uuid }))));
  }

  // ── Analysis metadata ────────────────────────────────────

  patchAnalysis(analysisId: string, organizationUuid: string | null, locationUuid: string | null, consultantUuid: string | null, body: PatchAnalysisRequestDto): Observable<AnalysisDetail> {
    const params: Record<string, string> = {};
    if (organizationUuid) params['organizationUuid'] = organizationUuid;
    if (locationUuid) params['locationUuid'] = locationUuid;
    if (consultantUuid) params['consultantUuid'] = consultantUuid;
    return this.http
      .patch<AnalysisDetailResponseDto>(`${this.baseUrl}analyses/${analysisId}`, body, { params })
      .pipe(map(toAnalysisDetail));
  }

  updateAnalysis(analysisId: string, formData: FormData): Observable<AnalysisDetail> {
    return this.http
      .put<AnalysisDetailResponseDto>(`${this.baseUrl}analyses/${analysisId}`, formData)
      .pipe(map(toAnalysisDetail));
  }

  importExcel(formData: FormData): Observable<{ analysisUuid: string }> {
    return this.http
      .post<{ data: { analysisUuid: string } }>(`${this.baseUrl}analyses/import/excel`, formData)
      .pipe(map(r => ({ analysisUuid: r.data.analysisUuid })));
  }

  // ── Risks ────────────────────────────────────────────────

  createRisk(analysisId: string, body: CreateRiskRequestDto): Observable<string> {
    return this.http
      .post<{ data: { uuid: string } }>(`${this.baseUrl}analyses/${analysisId}/risks`, body)
      .pipe(map(r => r.data.uuid));
  }

  updateRisk(analysisId: string, riskId: string, body: UpdateRiskRequestDto): Observable<void> {
    return this.http
      .patch<void>(`${this.baseUrl}analyses/${analysisId}/risks/${riskId}`, body);
  }

  deleteRisk(analysisId: string, riskId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}analyses/${analysisId}/risks/${riskId}`);
  }

  // ── Requirements ─────────────────────────────────────────

  createRequirement(analysisId: string, riskId: string, body: CreateRequirementRequestDto): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl}analyses/${analysisId}/risks/${riskId}/requirements`, body);
  }

  updateRequirement(analysisId: string, riskId: string, requirementId: string, body: UpdateRequirementRequestDto): Observable<void> {
    return this.http
      .patch<void>(`${this.baseUrl}analyses/${analysisId}/risks/${riskId}/requirements/${requirementId}`, body);
  }

  deleteRequirement(analysisId: string, riskId: string, requirementId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}analyses/${analysisId}/risks/${riskId}/requirements/${requirementId}`);
  }

  // ── Findings ─────────────────────────────────────────────

  createFinding(analysisId: string, data: CreateFindingDataDto, image?: File): Observable<void> {
    const fd = new FormData();
    fd.append('data', new Blob([JSON.stringify(data)], { type: 'application/json' }));
    if (image) {
      fd.append('image', image);
    }
    return this.http
      .post<void>(`${this.baseUrl}analyses/${analysisId}/findings`, fd);
  }

  updateFinding(analysisId: string, findingId: string, data: UpdateFindingDataDto, image?: File): Observable<void> {
    const fd = new FormData();
    fd.append('data', new Blob([JSON.stringify(data)], { type: 'application/json' }));
    if (image) {
      fd.append('image', image);
    }
    return this.http
      .patch<void>(`${this.baseUrl}analyses/${analysisId}/findings/${findingId}`, fd);
  }

  deleteFinding(analysisId: string, findingId: string): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}analyses/${analysisId}/findings/${findingId}`);
  }

  // ── Access Points ────────────────────────────────────────

  createAccessPoint(analysisId: string, body: CreateAccessPointRequestDto): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl}analyses/${analysisId}/access-points`, body);
  }

  updateAccessPoint(analysisId: string, apUuid: string, body: UpdateAccessPointRequestDto): Observable<void> {
    return this.http
      .patch<void>(`${this.baseUrl}analyses/${analysisId}/access-points/${apUuid}`, body);
  }

  deleteAccessPoint(analysisId: string, apUuid: string): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}analyses/${analysisId}/access-points/${apUuid}`);
  }

  // ── Security Functions ───────────────────────────────────

  createSecurityFunction(analysisId: string, body: CreateSecurityFunctionRequestDto): Observable<SecurityFunction> {
    return this.http
      .post<{ data: any }>(`${this.baseUrl}analyses/${analysisId}/security-functions`, body)
      .pipe(map(r => ({ ...r.data, id: r.data.uuid })));
  }

  updateSecurityFunction(analysisId: string, sfUuid: string, body: UpdateSecurityFunctionRequestDto): Observable<SecurityFunction> {
    return this.http
      .patch<{ data: any }>(`${this.baseUrl}analyses/${analysisId}/security-functions/${sfUuid}`, body)
      .pipe(map(r => ({ ...r.data, id: r.data.uuid })));
  }

  deleteSecurityFunction(analysisId: string, sfUuid: string): Observable<void> {
    return this.http
      .delete<void>(`${this.baseUrl}analyses/${analysisId}/security-functions/${sfUuid}`);
  }

  deleteAnalysis(analysisId: string): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}analyses/${analysisId}`);
  }

  uploadRiskImage(analysisId: string, riskId: string, image: File): Observable<{ id: number; imageUrl: string; displayOrder: number }> {
    const formData = new FormData();
    formData.append('image', image);
    return this.http
      .post<{ data: { riskImage: { id: number; imageUrl: string; displayOrder: number } } }>(
        `${this.baseUrl}analyses/${analysisId}/risks/${riskId}/images`,
        formData,
      )
      .pipe(map(r => r.data.riskImage));
  }

  deleteRiskImage(analysisId: string, riskId: string, imageId: number): Observable<void> {
    return this.http.delete<void>(
      `${this.baseUrl}analyses/${analysisId}/risks/${riskId}/images/${imageId}`,
    );
  }

  // ── Requirement Actions (state machine) ──────────────────

  assignRequirement(requirementUuid: string, assignToUserUuid: string): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl}requirements/${requirementUuid}/assign`, { assignToUserUuid });
  }

  startRequirement(requirementUuid: string): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl}requirements/${requirementUuid}/start`, {});
  }

  submitRequirement(requirementUuid: string, observations?: string, files?: File[], textEvidences?: string[], evidencesToDelete?: string[]): Observable<void> {
    const fd = new FormData();
    if (observations) {
      fd.append('observations', observations);
    }
    if (files?.length) {
      files.forEach(f => fd.append('files', f));
    }
    if (textEvidences?.length) {
      textEvidences.forEach(t => fd.append('textEvidences', t));
    }
    if (evidencesToDelete?.length) {
      evidencesToDelete.forEach(id => fd.append('evidencesToDelete', id));
    }
    return this.http
      .post<void>(`${this.baseUrl}requirements/${requirementUuid}/submit`, fd);
  }

  approveRequirement(requirementUuid: string): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl}requirements/${requirementUuid}/approve`, {});
  }

  rejectRequirement(requirementUuid: string, rejectionReason?: string): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl}requirements/${requirementUuid}/reject`, { rejectionReason });
  }

  reassignRequirement(requirementUuid: string, assignToUserUuid: string): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl}requirements/${requirementUuid}/reassign`, { assignToUserUuid });
  }

  unassignRequirement(requirementUuid: string): Observable<void> {
    return this.http
      .post<void>(`${this.baseUrl}requirements/${requirementUuid}/unassign`, {});
  }
}
